import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { i18n } from '../../translate/i18n';
import * as actions from "../../store/actions";
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import { Edit } from '@mui/icons-material';
import ModalSupportFiles from './ModalSupportFiles'
import moment from 'moment';
import filesize from "filesize";
import {
  Paper, Table, TableBody, TableRow, TableCell, Typography, IconButton, Button, Dialog, CircularProgress, TableFooter, TablePagination, Divider, Container,
} from '@mui/material';
import TablePaginationActionsWrapped from "../../UI/MaterialTableOverrides/TablePaginationActions";
import EnhancedTableHead from "../../UI/MaterialTableOverrides/EnhancedTableHead";
import { SaveAlt } from '@mui/icons-material';
import { styleModal } from '../../containers/Admin/Forms/styles'
import previewSelector from "../../utils/previewSelector";

export default function TableSupportFiles(props) {
  const dispatch = useDispatch();

  const styles = {
    table: {
      position: 'relative',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center'
    },
    avatar: {
      width: 50,
      height: 50,
    },
    avatarSmallText: {
      fontSize: 12,
      width: 50,
      height: 50,
    },
    chip: {
      margin: 5,
    },
    thumbUp: {
      marginRight: 15,
      color: "#349c34"
    },
    thumbDown: {
      marginRight: 15,
      color: "#d04545"
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    headerButtonsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    container: {
      padding: 10,
      margin: 0,
      marginLeft: 50,
      position: 'relative',
    },
  }

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState(false);
  const [modalContent, setModalContent] = useState({})
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("asc");

  const supportFiles = useSelector((state) => state.supportFiles.supportFiles)
  const loading = useSelector((state) => state.supportFiles.loading)
  const selectedAccess = useSelector((state) => state.auth.selectedAccess)

  const tenant = selectedAccess.tenant.name
  const role = selectedAccess.role

  const visibilityByRole = {
    admin: 'all',
    analyst: 'internal',
    doorman: 'none',
    contractor: 'external'
  };

  const visibility = visibilityByRole[role];
  
  const headRows = [
    { id: "description", numeric: false, disablePadding: false, label: i18n.t('headRowsSupport.description') },
    { id: "visibility", numeric: false, disablePadding: false, label: i18n.t('headRowsSupport.type'), showSort: false },
    { id: "updatedAt", numeric: false, disablePadding: false, label: i18n.t('headRowsSupport.updatedAt') },
    { id: "file", numeric: false, disablePadding: false, label: i18n.t('headRowsSupport.file'), showSort: false },
  ];

  if (role === 'admin') headRows.push({ id: "edit", numeric: false, disablePadding: false, label: i18n.t('headRowsSupport.admin'), showSort: false })

  const fetchSupportFiles = useCallback(() => dispatch(actions.fetchSupportFiles(page, limit, '', order, orderBy, visibility)));

  const setOpenModal = (open, type, doc = {}) => {
    setIsModalOpen(open);
    setModalType(type);
    setModalContent(doc)
  }

  useEffect(() => {
    fetchSupportFiles()
  }, [page, limit, order, orderBy])

  useEffect(() => {
   if (modalType === '') fetchSupportFiles()
  }, [modalType])

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleChangePage = (e, page) => {
    setPage(page + 1);
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
  };

  return (
    <Paper>
      <Dialog
        scroll='body'
        fullWidth
        open={isModalOpen}
        onClose={() => setOpenModal(false, 'close')}
        PaperProps={{
          style: styleModal,
        }}
      >
        <ModalSupportFiles
          type={modalType}
          closeModal={setOpenModal}
          modalContent={modalContent}
        />
      </Dialog>
      <div style={styles.headerButtonsContainer}>
        <Typography variant='h6' style={{ padding: 20 }}>{`${i18n.t('supportTypo.label')} ${tenant}`}</Typography>
        {role === 'admin' &&
          <Button
            style={{marginRight: 40}}
            variant='outlined'
            aria-label="add"
            onClick={() => setOpenModal(true, 'new', {})}
            startIcon={<AddCircleOutline />}
          >
            {i18n.t('supportBtn.label')}
          </Button>
        }
      </div>     

      <Table style={{...styles.table, opacity: loading ? 0.3 : 1}}>
        <tbody>
          <tr>
            <td>
              {loading &&
                <div style={styles.spinner}>
                  <CircularProgress />
                </div>
              }
              {supportFiles && supportFiles.rows.length === 0 &&
                <div style={styles.noResults}>
                  {!loading && <Typography style={styles.spinner}>{i18n.t('supportTypo.noResults')}</Typography>}
                </div>
              }
            </td>
          </tr>
        </tbody>
        <EnhancedTableHead
          style={{ opacity: loading ? '0.3' : '1' }}
          headRows={headRows}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />

        <TableBody style={styles.opacityContainer}>
          {supportFiles.rows.map((supportFile, index) => {
            return (
              <TableRow
                key={supportFile._id}
                hover
              >
                <TableCell>{supportFile.description}</TableCell>
                <TableCell>{supportFile.visibility === 'all' ? i18n.t('visibility.all') : supportFile.visibility === 'internal' ? i18n.t('visibility.internal') : i18n.t('visibility.external')}</TableCell>
                <TableCell>{moment(supportFile.file.updatedAt).format('DD/MM/YYYY')}</TableCell>
                
                <TableCell>
                  {supportFile.file.map((file, index) =>
                      <>
                        <div style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', paddingTop: 10, paddingBottom: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={previewSelector(file)} style={{ width: 36, height: 36, marginRight: 10 }} />
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <Typography style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', width: '13rem' }}>{file.name}</Typography>
                              <Typography style={{ fontSize: 12, color: 'grey' }}>{filesize(file.size)}</Typography>
                            </div>
                            <IconButton onClick={() => window.open(file.url)} size="large">
                              <SaveAlt />
                            </IconButton>
                          </div>
                        </div>
                      </>
                    )}   
                </TableCell>
                
                
                {role === 'admin'&&
                  <TableCell>
                    <IconButton
                      style={styles.actionButton}
                      aria-label="back"
                      onClick={() => setOpenModal(true, 'edit', supportFile)}
                      size="large">
                      <Edit />
                    </IconButton>
                  </TableCell>
                }
              </TableRow>
            );
          })}
        </TableBody>


         <TableFooter style={{ opacity: loading ? '0.3' : '1' }}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={supportFiles.count}
              rowsPerPage={parseInt(limit)}
              page={page - 1}
              SelectProps={{
                native: true
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActionsWrapped}
            />
          </TableRow>
        </TableFooter>

      </Table> 
    </Paper>
  );

}



